import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components';
import { SupportTicketFormDialog } from '@/components/doctor/SupportTicketFormDialog.tsx';
import { useAuthFetch } from '@/hooks';
import type { DoctorSupportTicketsResponse, SupportTicketCategory, SupportTicketStatus } from '@dermalink/shared';
import styles from './DoctorSupportTickets.module.css';

function categoryLabel(category: SupportTicketCategory): string {
  return category === 'feedback' ? 'Feedback' : 'Ondersteuning';
}

function statusLabel(status: SupportTicketStatus): string {
  return status === 'open' ? 'Open' : 'Opgelost';
}

function statusClass(status: SupportTicketStatus): string {
  return status === 'open' ? styles.pillOpen : styles.pillResolved;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function DoctorSupportTickets() {
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery<DoctorSupportTicketsResponse>({
    queryKey: ['doctor-support-tickets'],
    queryFn: async () => {
      const res = await authFetch('/doctor/support-tickets');
      if (!res.ok) throw new Error();
      return (await res.json()) as DoctorSupportTicketsResponse;
    },
  });

  const tickets = data?.tickets ?? [];

  const postMutation = useMutation({
    mutationFn: async ({ category, subject, body }: { category: SupportTicketCategory; subject: string; body: string }) => {
      const res = await authFetch('/doctor/support-tickets', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ category, subject, body }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? 'Versturen mislukt');
      }
    },
    onSuccess: () => { setOpen(false); setFormError(null); queryClient.invalidateQueries({ queryKey: ['doctor-support-tickets'] }); },
    onError: (e: Error) => setFormError(e.message),
  });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className="page-heading">Feedback & support</h1>
          <p className={`text-muted ${styles.subheading}`}>Stuur feedback over het platform of stel een vraag aan het Dermalink-team.</p>
        </div>
        <Button type="button" variant="primary" onClick={() => { setFormError(null); setOpen(true); }}>Nieuw ticket</Button>
      </div>

      {isPending && <p className="text-muted status-message">Laden...</p>}
      {isError && <p className="text-muted status-message">Kon tickets niet laden.</p>}
      {!isPending && !isError && tickets.length === 0 && <p className="text-muted status-message">Nog geen tickets ingediend.</p>}

      {!isPending && !isError && tickets.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Type</th>
                <th>Onderwerp</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id}>
                  <td>{formatDate(t.created_at)}</td>
                  <td>{categoryLabel(t.category)}</td>
                  <td>{t.subject}</td>
                  <td><span className={`${styles.pill} ${statusClass(t.status)}`}>{statusLabel(t.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SupportTicketFormDialog
        open={open}
        submitting={postMutation.isPending}
        serverError={formError}
        onClose={() => { if (postMutation.isPending) return; setOpen(false); setFormError(null); }}
        onSubmit={(category, subject, body) => { setFormError(null); postMutation.mutate({ category, subject, body }); }}
      />
    </div>
  );
}
